import { useEffect, useState, useRef, useCallback } from "react";
import "./YesterdayContent.css";
import { StayFeedLoadingPlaceholder } from "../../../../Screens/DashboardView/MasterTemplateEditor/WebsiteLibFeedSkeleton";
import { debounce } from "lodash";
import { useParams } from "react-router-dom";
import {
  handleFirstImage,
  handleNavigate,
} from "../../../../Utils/StayUpToDate/RenderComponent";
import { updateIsEmptyStatus } from "../../../../Utils/StayUpToDate/RenderComponent";
import FrontendTemplate from "../StructureTemplates/FrontendTemplate";
import PostTemplate from "../CommonComponents/PostTemplate";
import { useLoadYesterdayContent } from "../../../../Utils/customHooks/UpToDateContent/useLoadYesterdayContent";
import { FeedSectionEmptyState } from "../FeedSectionEmptyState";
import { websiteEditorFeedEmptyMessage } from "../../../../Screens/DashboardView/MasterTemplateEditor/websiteEditorFeedEmptyMessage";

const YesterdayContentFront = (props: any) => {
  const itemsPerPage = 3;
  const {
    user_id,
    username,
    numberOfDays,
    title,
    isLinks,
    handleRedirect,
    postIds,
    brand_color,
    removedPostIds,
    isHandleNavigate,
    component,
    type,
    RenderUI,
    from,
    designTokens,
    onFeedResolved,
    showEmptyInEditor = false,
  } = props;
  const param = useParams();
  const [currentIndex, setCurrentIndex] = useState(0);
  const containerRef = useRef<any>(null);
  const isButtonClicked = useRef(false);
  const shouldAutoScrollRef = useRef(true);
  const [isLoading, setIsLoading] = useState(true);

  const resolvedDays = (() => {
    if (Array.isArray(numberOfDays) && numberOfDays.length > 0) {
      return numberOfDays;
    }
    const dn = String(component?.display_name ?? "").trim();
    if (dn === "Yesterday") return [1];
    if (dn === "This Week Content" || dn === "This Week") return [7];
    if (dn === "This Month") return [30];
    return null;
  })();

  const { 
    isLoading: yesterdayContentLoading, 
    data: yesterdayContentData,
    total_count: totalCount,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  }: any = useLoadYesterdayContent(
      username,
      user_id,
      resolvedDays,
      component?.post_ids,
      component?.removed_postids,
      component?.name,
      false,
      component?.content_order,
      component?.custom_order?.split(",")
    );

  const isTemplateWebsite = from === "website-template";

  // Website: prefetch at most one extra page for a fuller first paint.
  // Do not exhaust the day-window up front — that contended with other tabs and
  // delayed first paint; remaining pages load via scroll / fetchNextPage.
  useEffect(() => {
    if (!isTemplateWebsite) return;
    if (yesterdayContentLoading || isFetchingNextPage) return;
    if (!hasNextPage || typeof fetchNextPage !== "function") return;
    if ((yesterdayContentData?.length ?? 0) > 25) return;
    fetchNextPage();
  }, [
    isTemplateWebsite,
    yesterdayContentLoading,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
    yesterdayContentData?.length,
  ]);
 
  useEffect(() => {
    const checkEmptyStatus = async () => {
      if (!yesterdayContentLoading) {
        if (!yesterdayContentData || yesterdayContentData.length === 0 || totalCount === 0) {
          if (!showEmptyInEditor) {
            await updateIsEmptyStatus(title, user_id, component.id,from);
          }
          setIsLoading(false);
        } else {
          // Paint as soon as the first page is ready — card-image write is not critical path.
          setIsLoading(false);
          void handleFirstImage(
            title,
            component?.id,
            yesterdayContentData[0],
            user_id,
            from
          );
        }
      }
    };
    checkEmptyStatus();
  }, [yesterdayContentLoading, yesterdayContentData, totalCount]);

  useEffect(() => {
    shouldAutoScrollRef.current = true;
  }, [totalCount]);

  const handlePrevPage = () => {
    isButtonClicked.current = true;
    setCurrentIndex((prevIndex) => Math.max(prevIndex - 1, 0));
    setTimeout(() => {
      isButtonClicked.current = false;
    }, 500);
  };

  const handleNextPage = () => {
    isButtonClicked.current = true;
    setCurrentIndex((prevIndex) =>
      Math.min(prevIndex + 1, totalCount ? totalCount - itemsPerPage : 0)
    );
    setTimeout(() => {
      isButtonClicked.current = false;
    }, 500);
  };

  const handleScroll = useCallback(
    debounce(() => {
      if (!isButtonClicked.current && totalCount) {
        if (containerRef.current) {
          shouldAutoScrollRef.current = false;
          const scrollLeft = containerRef?.current?.scrollLeft;
          const itemWidth =
            containerRef?.current?.scrollWidth /
            totalCount;
          const newCurrentIndex = Math.round(scrollLeft / itemWidth);
          setCurrentIndex(newCurrentIndex);
        }
      }
    }, 100),
    [totalCount]
  );

  useEffect(() => {
    if (containerRef.current && totalCount && shouldAutoScrollRef.current) {
      return;
    }
    const itemWidth = containerRef?.current?.scrollWidth / totalCount;
    if (Number.isFinite(itemWidth)) {
      const scrollPosition = itemWidth * currentIndex;
      if (typeof containerRef?.current?.scrollTo === "function") {
        containerRef.current.scrollTo({
          left: scrollPosition,
          behavior: isButtonClicked.current ? "smooth" : "auto",
        });
      } else {
        containerRef.current.scrollLeft = scrollPosition;
      }
    }
    shouldAutoScrollRef.current = false;
  }, [currentIndex, totalCount]);

  useEffect(() => {
    if (containerRef.current && totalCount) {
      containerRef.current.addEventListener("scroll", handleScroll);
      return () => {
        if (containerRef.current) {
          containerRef.current.removeEventListener("scroll", handleScroll);
        }
      };
    }
  }, [totalCount, handleScroll]);

  useEffect(() => {
    if (yesterdayContentLoading) return;
    onFeedResolved?.(
      (yesterdayContentData?.length ?? 0) > 0 && (totalCount ?? 0) > 0,
    );
  }, [
    yesterdayContentLoading,
    yesterdayContentData?.length,
    totalCount,
    onFeedResolved,
  ]);

  return (
    <>
      {yesterdayContentLoading || isLoading ? (
        <StayFeedLoadingPlaceholder
          from={from}
          component={component}
          designTokens={designTokens}
        />
      ) : (!yesterdayContentData || yesterdayContentData.length === 0 || totalCount === 0) ? (
        showEmptyInEditor ? (
          <FeedSectionEmptyState
            title={title}
            designTokens={designTokens}
            message={websiteEditorFeedEmptyMessage("posts")}
          />
        ) : null
      ) : (
        <>
          {RenderUI ? (
            <RenderUI
              data={yesterdayContentData}
              currentIndex={currentIndex}
              handleNavigate={handleNavigate}
              component={component}
              handleNextPage={handleNextPage}
              handlePrevPage={handlePrevPage}
              containerRef={containerRef}
              handleRedirect={handleRedirect}
              brand_color={brand_color}
              title={title}
              type={type}
              componentType={
                component?.type || component?.display_name || type || ""
              }
              isLinks={isLinks}
              from={from}
              username={username}
              user_id={user_id}
              fetchNextPage={isTemplateWebsite ? fetchNextPage : undefined}
              hasNextPage={isTemplateWebsite ? hasNextPage : false}
              isFetchingNextPage={isTemplateWebsite ? isFetchingNextPage : false}
              designTokens={designTokens}
            />
          ) : yesterdayContentData && totalCount > 0 ? (
            <PostTemplate
              processedData={yesterdayContentData}
              total_count={totalCount}
              component={component}
              user_id={user_id}
              username={username}
              handleRedirect={handleRedirect}
              brand_color={brand_color}
              title={title}
              from={from}
              type={component?.type || component?.display_name || type || ""}
              itemsPerPage={itemsPerPage}
              pinnedPostIds={component?.pinned_posts?.split(",") || []}
              isLinks={isLinks}
              designTokens={designTokens}
            />
          ) : (
            ""
          )}
        </>
      )}
    </>
  );
};

export default YesterdayContentFront;
